import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { XIcon } from '@heroicons/react/solid'

import TagAutocomplete from '@/shared/TagAutocomplete'
import useQuery from '@/hooks/useQuery'

// Each tag kind the role can carry. `voiceOnly` kinds only show for roles
// people audition to with their voice or body.
const TAG_KINDS = [
  { key: 'genderAges', kind: 'user-gender-age', label: 'Voice age & gender', placeholder: 'Female young adult, male teen, baby', voiceOnly: true },
  { key: 'accents', kind: 'user-accents', label: 'Accents', placeholder: 'Texan, Irish, French', voiceOnly: true },
  { key: 'demoCategories', kind: 'demo-categories', label: 'Voice description', placeholder: 'Warm, funny, Robin Williams', voiceOnly: true },
  { key: 'languages', kind: 'user-languages', label: 'Languages', placeholder: 'English, Japanese' },
  { key: 'skills', kind: 'user-skills', label: 'Skills', placeholder: 'Figma, Reaper, ADR' },
  { key: 'softwares', kind: 'user-softwares', label: 'Software', placeholder: 'Audacity, Source Connect, Scrivener' },
  { key: 'hardwares', kind: 'user-hardwares', label: 'Hardware', placeholder: 'Scarlett 2i2, Shure SM7b, Blue Yeti' }
]
const KIND_TO_KEY = Object.fromEntries(TAG_KINDS.map((k) => [k.kind, k.key]))
const VOICE_KINDS = ['Voice Actor', 'Singer', 'Actor']

const RoleTags = ({ roleId, roleKind }) => {
  const { getRequest, putpostRequest } = useQuery()
  const [tags, setTags] = useState(Object.fromEntries(TAG_KINDS.map((k) => [k.key, []])))

  useEffect(() => {
    getRequest('/api/v3/labelings/role_labels', { role_id: roleId }, (err, jsonData) => {
      if (err) { /* hooks */ return }
      setTags(Object.fromEntries(TAG_KINDS.map((k) => [k.key, jsonData[k.key] || []])))
    })
  }, [roleId])

  const addLabel = (label, labelKind) => {
    const key = KIND_TO_KEY[labelKind]
    putpostRequest('/api/v3/labelings', 'POST', { labeling: { labeled_kind: 'Role', labeled_id: roleId }, label: { name: label, label_kind: labelKind } }, (err, jsonData) => {
      if (err) { /* hook */ return }
      setTags((t) => ({ ...t, [key]: [...t[key], jsonData.labeling] }))
    })
  }

  const removeLabel = (labelingId, key) => {
    setTags((t) => ({ ...t, [key]: t[key].filter((l) => l.id !== labelingId) }))
    putpostRequest(`/api/v3/labelings/${labelingId}`, 'DELETE', {}, () => {})
  }

  const visible = TAG_KINDS.filter((k) => !k.voiceOnly || VOICE_KINDS.includes(roleKind))

  return <div>
    <h4 className='text-sm font-semibold text-gray-900 dark:text-white'>Tags</h4>
    <p className='text-xs text-gray-500 dark:text-gray-400'>These power search filters. Leave one blank if you're open to anything. Open to any accent? Skip accents.</p>
    <div className='mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
      { visible.map((k) => (
        <div key={k.key}>
          <label className='block text-xs font-semibold text-gray-600 dark:text-gray-300'>{k.label}</label>
          <TagAutocomplete kind={k.kind} addLabel={addLabel} placeholder={k.placeholder} />
          { tags[k.key].length > 0 && <div className='mt-2 flex flex-wrap gap-1'>
            { tags[k.key].map((labeling) => (
              <button key={labeling.id} type='button' onClick={() => removeLabel(labeling.id, k.key)} title='Remove'
                className='inline-flex items-center gap-x-1 rounded-full bg-cccblue px-2.5 py-0.5 text-xs font-semibold capitalize text-white hover:bg-cccblue-alt'>
                {labeling.name} <XIcon className='h-3.5 w-3.5' />
              </button>
            ))}
          </div> }
        </div>
      ))}
    </div>
  </div>
}

export default RoleTags

RoleTags.propTypes = {
  roleId: PropTypes.number.isRequired,
  roleKind: PropTypes.string.isRequired
}
